import {FC, ReactNode} from "react";
import {Chip, ChipProps, SxProps, Theme, useColorScheme} from "@mui/material";
import {styled} from "@mui/material/styles";
import {t} from "../tokens";

export type ChipStatus = "success" | "warning" | "error" | "info" | "neutral";
export type ChipVariant = "outlined" | "soft" | "solid";
export type ChipSize = "small" | "medium";

interface MyChipProps {
    label: string;
    status?: ChipStatus;
    variant?: ChipVariant;
    size?: ChipSize;
    withDot?: boolean;
    icon?: ReactNode;
    onDelete?: () => void;
    onClick?: () => void;
    sx?: SxProps<Theme>;
    /**
     * ARIA для кликабельного чипа. Нужны, когда чип не просто кнопка, а
     * открывает меню: скринридеру надо назвать действие (label) и сообщить,
     * что за кликом всплывёт список (haspopup) и раскрыт ли он сейчас.
     * Чип-индикатор (без onClick) их не задаёт.
     */
    ariaLabel?: string;
    ariaHasPopup?: "menu" | "listbox" | "dialog" | true;
    ariaExpanded?: boolean;
}

/**
 * Маппинг статуса в группу токенов.
 * Для neutral используется text.secondary (нет собственного цвета — это и есть смысл нейтрали).
 */
const STATUS_TOKENS = {
    success: {main: t.success.main, alpha: t.success.alpha},
    warning: {main: t.warning.main, alpha: t.warning.alpha},
    error: {main: t.error.main, alpha: t.error.alpha},
    info: {main: t.info.main, alpha: t.info.alpha},
    neutral: {main: t.text.secondary, alpha: t.text.secondaryAlpha},
} as const;

const SIZING = {
    small: {height: 22, fontSize: "0.75rem", paddingX: 8},
    medium: {height: 26, fontSize: "0.8125rem", paddingX: 10},
} as const;

interface StyledChipProps extends Omit<ChipProps, "variant" | "size"> {
    chipStatus: ChipStatus;
    chipVariant: ChipVariant;
    chipSize: ChipSize;
    isDark: boolean;
    isClickable: boolean;
}

const StyledChip = styled(Chip, {
    shouldForwardProp: (prop) =>
        prop !== "chipStatus" &&
        prop !== "chipVariant" &&
        prop !== "chipSize" &&
        prop !== "isDark" &&
        prop !== "isClickable",
})<StyledChipProps>(({chipStatus, chipVariant, chipSize, isDark, isClickable}) => {
    const tokens = STATUS_TOKENS[chipStatus];
    const {height, fontSize, paddingX} = SIZING[chipSize];

    let bg: string;
    let border: string;
    let color: string;
    let hoverBg: string;

    switch (chipVariant) {
        case "outlined":
            bg = "transparent";
            border = tokens.alpha(isDark ? 0.6 : 0.45);
            color = tokens.main;
            hoverBg = tokens.alpha(isDark ? 0.10 : 0.06);
            break;
        case "soft":
            bg = tokens.alpha(isDark ? 0.16 : 0.12);
            border = "transparent";
            color = tokens.main;
            hoverBg = tokens.alpha(isDark ? 0.22 : 0.18);
            break;
        case "solid":
            bg = tokens.main;
            border = "transparent";
            color = t.common.white;
            hoverBg = tokens.alpha(0.85);
            break;
    }

    return {
        height,
        fontSize,
        fontWeight: 500,
        borderRadius: t.radius.sm,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color,
        fontVariantNumeric: "tabular-nums",
        transition: [
            `background-color ${t.duration.base}ms`,
            `border-color ${t.duration.base}ms`,
            `color ${t.duration.base}ms`,
        ].join(", "),

        "& .MuiChip-label": {
            paddingLeft: paddingX,
            paddingRight: paddingX,
            display: "flex",
            alignItems: "center",
            gap: 6,
            lineHeight: 1,
        },

        "& .MuiChip-deleteIcon": {
            color: "inherit",
            opacity: 0.6,
            marginRight: 4,
            marginLeft: -2,
            fontSize: 14,
            "&:hover": {opacity: 1},
        },

        ...(isClickable && {
            cursor: "pointer",
            "&:hover": {backgroundColor: hoverBg},
        }),
    };
});

const MyChip: FC<MyChipProps> = ({
                                     label,
                                     status = "neutral",
                                     variant = "outlined",
                                     size = "small",
                                     withDot = true,
                                     icon,
                                     onDelete,
                                     onClick,
                                     sx,
                                     ariaLabel,
                                     ariaHasPopup,
                                     ariaExpanded,
                                 }) => {
    // useColorScheme работает в режиме cssVariables и возвращает 'light' | 'dark'
    // без перерендера-каскада. Используем его, чтобы выбрать правильную
    // непрозрачность фона/бордера для текущей темы.
    const {mode, systemMode} = useColorScheme();
    const isDark = (mode === "system" ? systemMode : mode) === "dark";

    const dotColor = STATUS_TOKENS[status].main;
    const showDot = withDot && !icon;

    return (
        <StyledChip
            chipStatus={status}
            chipVariant={variant}
            chipSize={size}
            isDark={isDark}
            isClickable={!!onClick}
            onClick={onClick}
            onDelete={onDelete}
            sx={sx}
            aria-label={ariaLabel}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            label={
                <>
                    {showDot && (
                        <span
                            aria-hidden
                            style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: dotColor,
                                flexShrink: 0,
                                display: "inline-block",
                            }}
                        />
                    )}
                    {icon}
                    <span>{label}</span>
                </>
            }
        />
    );
};

export default MyChip;